import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Briefcase, 
  Users, 
  Globe,
  Heart,
  Star,
  ArrowRight,
  MapPin,
  Clock,
  Award,
  Target,
  Zap,
  Coffee
} from 'lucide-react';

const CareersPage = () => {
  const openPositions = [
    {
      title: "Concierge de Luxe - Europe",
      location: "Monaco",
      type: "CDI",
      department: "Service Client",
      description: "Rejoignez notre équipe d'experts pour offrir un service exceptionnel à notre clientèle haut de gamme européenne.",
      requirements: ["5+ ans expérience luxury", "Trilingue (FR/EN/IT)", "Réseau hôtellerie"],
      salary: "€45k - €65k"
    },
    {
      title: "Curator de Villas - Caraïbes",
      location: "Saint-Barthélemy",
      type: "CDI",
      department: "Acquisition",
      description: "Identifiez et sélectionnez les plus belles propriétés des Caraïbes selon nos standards d'excellence.",
      requirements: ["Expertise immobilier luxury", "Connaissance Caraïbes", "Anglais natif"],
      salary: "$60k - $85k"
    },
    {
      title: "Designer d'Expériences",
      location: "Miami / Remote",
      type: "CDI",
      department: "Produit",
      description: "Créez des expériences impossibles et personnalisées pour nos clients les plus exigeants.",
      requirements: ["Créativité exceptionnelle", "Réseau international", "Event planning"],
      salary: "$55k - $75k"
    }
  ];

  const benefits = [
    {
      icon: Globe,
      title: "Voyages Gratuits",
      description: "Séjours dans nos villas pour tester nos services"
    },
    {
      icon: Star,
      title: "Formation Continue",
      description: "Programmes de développement avec experts luxury"
    },
    {
      icon: Users,
      title: "Équipe Internationale",
      description: "Collaborez avec des talents du monde entier"
    },
    {
      icon: Heart,
      title: "Bien-être",
      description: "Assurance premium et services de conciergerie"
    },
    {
      icon: Target,
      title: "Impact Direct",
      description: "Votre travail crée des souvenirs inoubliables"
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "Environnement startup avec moyens luxury"
    }
  ];

  const values = [
    {
      title: "Excellence Sans Compromis",
      description: "Nous visons la perfection dans chaque détail, car nos clients méritent l'exceptionnel."
    },
    {
      title: "Authenticité Émotionnelle", 
      description: "Nous créons des connexions vraies entre nos clients et leurs destinations de rêve."
    },
    {
      title: "Innovation Responsable",
      description: "Nous réinventons le luxury travel tout en respectant l'environnement et les communautés locales."
    },
    {
      title: "Équipe Comme Famille",
      description: "Nous nous soutenons mutuellement pour offrir le meilleur service à nos clients."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1600607687644-c7171b42498b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <Briefcase className="w-4 h-4 mr-2" />
            Rejoignez-nous
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Carrières PRIVIA
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Rejoignez l'équipe qui réinvente le luxe et crée des expériences impossibles pour une clientèle d'exception
          </p>

          <Button 
            size="lg"
            className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Voir les Postes Ouverts
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Nos Valeurs
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Ce qui nous guide chaque jour dans notre mission d'excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="p-8 hover:shadow-lg transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                <h3 
                  className="text-xl font-light text-gray-800 mb-4"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Pourquoi PRIVIA ?
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Des avantages exceptionnels pour une équipe d'exception
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-medium mb-4">{benefit.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Postes Ouverts
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Trouvez le rôle qui correspond à vos talents et ambitions
            </p>
          </div>

          <div className="space-y-6">
            {openPositions.map((position, index) => (
              <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <Badge variant="outline" className="text-xs">
                        {position.department}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="w-4 h-4" />
                        <span>{position.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>{position.type}</span>
                      </div>
                    </div>
                    
                    <h3 
                      className="text-2xl font-light text-gray-800 mb-4"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {position.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {position.description}
                    </p>
                    
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Requis:</p>
                      <div className="flex flex-wrap gap-2">
                        {position.requirements.map((req, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {req}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="lg:text-right">
                    <p className="text-2xl font-light text-amber-600 mb-4">
                      {position.salary}
                    </p>
                    <Button 
                      className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-6 py-2 rounded-full font-medium transition-all duration-300"
                    >
                      Postuler
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              Vous ne trouvez pas le poste idéal ? Envoyez-nous votre candidature spontanée.
            </p>
            <Button 
              variant="outline"
              className="border-2 border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white px-8 py-3 rounded-full font-medium transition-all duration-300"
            >
              Candidature Spontanée
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;